import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

const LLMConfigurationManager = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  const [showAddModel, setShowAddModel] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [newModel, setNewModel] = useState({
    id: '',
    displayName: '',
    provider: 'vertex_ai',
    contextWindow: 1000000,
    maxOutputTokens: 8192,
    isActive: true
  });

  const token = localStorage.getItem('token');

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getLLMConfiguration(token);
      setConfig(data);
    } catch (err) {
      console.error('Error fetching LLM configuration:', err);
      setError(err.message || 'Failed to load LLM configuration');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await api.updateLLMConfiguration(config, token);
      setSuccess('Configuration saved successfully');
    } catch (err) {
      console.error('Error saving LLM configuration:', err);
      setError(err.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleAddModel = async () => {
    if (!newModel.id || !newModel.displayName) {
      setError('Model ID and Display Name are required');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await api.addAvailableModel(newModel, token);
      setSuccess(`Model "${newModel.displayName}" added successfully`);
      setShowAddModel(false);
      setNewModel({
        id: '',
        displayName: '',
        provider: 'vertex_ai',
        contextWindow: 1000000,
        maxOutputTokens: 8192,
        isActive: true
      });
      fetchConfig();
    } catch (err) {
      console.error('Error adding model:', err);
      setError(err.message || 'Failed to add model');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateModel = async (provider, modelId, updates) => {
    try {
      setSaving(true);
      setError(null);
      await api.updateAvailableModel(provider, modelId, updates, token);
      setSuccess('Model updated successfully');
      setEditingModel(null);
      fetchConfig();
    } catch (err) {
      console.error('Error updating model:', err);
      setError(err.message || 'Failed to update model');
    } finally {
      setSaving(false);
    }
  };

  // A deprecated model can be brought back. The Activate toggle alone left it
  // deprecated, and a deprecated model is never offered for selection.
  const handleReactivateModel = (provider, modelId) =>
    handleUpdateModel(provider, modelId, { isActive: true, isDeprecated: false, deprecationMessage: null });

  const startEditModel = (model) => {
    setError(null);
    setEditingModel({
      provider: model.provider,
      id: model.id,
      displayName: model.displayName || '',
      contextWindow: model.contextWindow ?? '',
      maxOutputTokens: model.maxOutputTokens ?? ''
    });
  };

  // ID and provider are fixed on the server; only these three fields change.
  const handleSaveEdit = async () => {
    const displayName = String(editingModel.displayName || '').trim();
    const contextWindow = parseInt(editingModel.contextWindow, 10);
    const maxOutputTokens = parseInt(editingModel.maxOutputTokens, 10);
    if (!displayName) {
      setError('Display Name is required');
      return;
    }
    if (!(contextWindow > 0) || !(maxOutputTokens > 0)) {
      setError('Context Window and Max Output Tokens must be positive whole numbers');
      return;
    }
    await handleUpdateModel(editingModel.provider, editingModel.id, { displayName, contextWindow, maxOutputTokens });
  };

  const handleDeprecateModel = async (provider, modelId) => {
    if (!window.confirm('Are you sure you want to deprecate this model? It will no longer be available for selection.')) {
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await api.removeAvailableModel(provider, modelId, false, token);
      setSuccess('Model deprecated successfully');
      fetchConfig();
    } catch (err) {
      console.error('Error deprecating model:', err);
      setError(err.message || 'Failed to deprecate model');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveModel = async (provider, modelId) => {
    if (!window.confirm('Are you sure you want to permanently remove this model? This action cannot be undone.')) {
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await api.removeAvailableModel(provider, modelId, true, token);
      setSuccess('Model removed successfully');
      fetchConfig();
    } catch (err) {
      console.error('Error removing model:', err);
      setError(err.message || 'Failed to remove model');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={styles.container}>Loading LLM configuration...</div>;
  }

  if (!config) {
    return <div style={styles.container}>Failed to load configuration</div>;
  }

  // Filter models by provider for dropdowns
  const getModelsForProvider = (provider) => {
    return (config.availableModels || []).filter(
      m => m.provider === provider && m.isActive && !m.isDeprecated
    );
  };

  const vertexModels = getModelsForProvider('vertex_ai');
  const geminiModels = getModelsForProvider('gemini');
  const openaiModels = getModelsForProvider('openai');

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>LLM Configuration</h2>
      <p style={styles.subtitle}>
        Configure the AI models used for therapist responses and simulation.
      </p>

      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      {/* Tabs */}
      <div style={styles.tabs}>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'general' ? styles.tabActive : {})
          }}
          onClick={() => setActiveTab('general')}
        >
          General Settings
        </button>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'generation' ? styles.tabActive : {})
          }}
          onClick={() => setActiveTab('generation')}
        >
          Generation Parameters
        </button>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'simulator' ? styles.tabActive : {})
          }}
          onClick={() => setActiveTab('simulator')}
        >
          Simulator Defaults
        </button>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'reviews' ? styles.tabActive : {})
          }}
          onClick={() => setActiveTab('reviews')}
        >
          Review Defaults
        </button>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'models' ? styles.tabActive : {})
          }}
          onClick={() => setActiveTab('models')}
        >
          Manage Models
        </button>
      </div>

      {/* General Settings Tab */}
      {activeTab === 'general' && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Provider & Model Selection</h3>
          <p style={styles.description}>
            Select the primary and fallback LLM providers for therapist responses.
            Only Google Gemini models are available due to their large context window support.
          </p>

          <div style={styles.formGroup}>
            <label style={styles.label}>Primary Provider</label>
            <select
              value={config.primaryProvider || 'vertex_ai'}
              onChange={(e) => setConfig({ ...config, primaryProvider: e.target.value })}
              style={styles.select}
            >
              <option value="vertex_ai">Vertex AI (Google Cloud)</option>
              <option value="gemini">Gemini API (Direct)</option>
            </select>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Primary Model</label>
            <select
              value={config.primaryModel || 'gemini-2.5-flash'}
              onChange={(e) => setConfig({ ...config, primaryModel: e.target.value })}
              style={styles.select}
            >
              {(config.primaryProvider === 'gemini' ? geminiModels : vertexModels).map(m => (
                <option key={m.id} value={m.id}>
                  {m.displayName} ({m.contextWindow?.toLocaleString()} tokens)
                </option>
              ))}
            </select>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.toggleLabel}>
              <input
                type="checkbox"
                checked={config.fallbackEnabled !== false}
                onChange={(e) => setConfig({ ...config, fallbackEnabled: e.target.checked })}
                style={styles.checkbox}
              />
              <span>Enable Fallback Provider</span>
            </label>
            <p style={styles.hint}>
              When enabled, if the primary provider fails, the system will automatically try the fallback.
            </p>
          </div>

          {config.fallbackEnabled !== false && (
            <>
              <div style={styles.formGroup}>
                <label style={styles.label}>Fallback Provider</label>
                <select
                  value={config.fallbackProvider || 'gemini'}
                  onChange={(e) => setConfig({ ...config, fallbackProvider: e.target.value })}
                  style={styles.select}
                >
                  <option value="vertex_ai">Vertex AI (Google Cloud)</option>
                  <option value="gemini">Gemini API (Direct)</option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Fallback Model</label>
                <select
                  value={config.fallbackModel || 'gemini-2.0-flash'}
                  onChange={(e) => setConfig({ ...config, fallbackModel: e.target.value })}
                  style={styles.select}
                >
                  {(config.fallbackProvider === 'vertex_ai' ? vertexModels : geminiModels).map(m => (
                    <option key={m.id} value={m.id}>
                      {m.displayName} ({m.contextWindow?.toLocaleString()} tokens)
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          <button onClick={handleSave} disabled={saving} style={styles.saveButton}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      )}

      {/* Generation Parameters Tab */}
      {activeTab === 'generation' && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Default Generation Parameters</h3>
          <p style={styles.description}>
            These parameters control how the AI generates responses. Personas can override these settings.
          </p>

          <div style={styles.formGroup}>
            <label style={styles.label}>
              Temperature: {config.defaultTemperature?.toFixed(1) || '0.7'}
            </label>
            <input
              type="range"
              min="0"
              max="2"
              step="0.1"
              value={config.defaultTemperature || 0.7}
              onChange={(e) => setConfig({ ...config, defaultTemperature: parseFloat(e.target.value) })}
              style={styles.slider}
            />
            <p style={styles.hint}>
              Lower values (0-0.5) produce more focused, deterministic responses.
              Higher values (1-2) produce more creative, varied responses.
            </p>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Max Output Tokens: {config.defaultMaxTokens || 2048}</label>
            <input
              type="range"
              min="256"
              max="8192"
              step="256"
              value={config.defaultMaxTokens || 2048}
              onChange={(e) => setConfig({ ...config, defaultMaxTokens: parseInt(e.target.value) })}
              style={styles.slider}
            />
            <p style={styles.hint}>
              Maximum length of AI responses. Higher values allow longer responses but may increase costs.
            </p>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>
              Top-P (Nucleus Sampling): {config.defaultTopP?.toFixed(2) || '0.90'}
            </label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={config.defaultTopP || 0.9}
              onChange={(e) => setConfig({ ...config, defaultTopP: parseFloat(e.target.value) })}
              style={styles.slider}
            />
            <p style={styles.hint}>
              Controls diversity via nucleus sampling. Lower values make output more focused.
            </p>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Top-K (Gemini only)</label>
            <input
              type="number"
              min="1"
              max="100"
              value={config.defaultTopK || ''}
              onChange={(e) => setConfig({
                ...config,
                defaultTopK: e.target.value ? parseInt(e.target.value) : null
              })}
              placeholder="Leave empty for model default"
              style={styles.input}
            />
            <p style={styles.hint}>
              Limits the number of token choices. Leave empty to use the model's default.
            </p>
          </div>

          <button onClick={handleSave} disabled={saving} style={styles.saveButton}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      )}

      {/* Simulator Defaults Tab */}
      {activeTab === 'simulator' && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Simulator Default Settings</h3>
          <p style={styles.description}>
            Default model settings for the Advanced Simulation system.
            These can be overridden per simulation run.
          </p>

          <div style={styles.formGroup}>
            <label style={styles.label}>Default Client Model (OpenAI)</label>
            <select
              value={config.simulatorClientModel || 'gpt-4o'}
              onChange={(e) => setConfig({ ...config, simulatorClientModel: e.target.value })}
              style={styles.select}
            >
              {openaiModels.map(m => (
                <option key={m.id} value={m.id}>
                  {m.displayName} ({m.contextWindow?.toLocaleString()} tokens)
                </option>
              ))}
            </select>
            <p style={styles.hint}>
              The OpenAI model used to simulate client responses in testing.
            </p>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>
              Client Temperature: {config.simulatorClientTemperature?.toFixed(1) || '0.7'}
            </label>
            <input
              type="range"
              min="0"
              max="2"
              step="0.1"
              value={config.simulatorClientTemperature || 0.7}
              onChange={(e) => setConfig({
                ...config,
                simulatorClientTemperature: parseFloat(e.target.value)
              })}
              style={styles.slider}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Default Therapist Model</label>
            <select
              value={config.simulatorTherapistModel || 'gemini-2.5-flash'}
              onChange={(e) => setConfig({ ...config, simulatorTherapistModel: e.target.value })}
              style={styles.select}
            >
              {vertexModels.map(m => (
                <option key={m.id} value={m.id}>
                  {m.displayName} ({m.contextWindow?.toLocaleString()} tokens)
                </option>
              ))}
            </select>
            <p style={styles.hint}>
              The Gemini model used for therapist responses in simulation.
            </p>
          </div>

          <button onClick={handleSave} disabled={saving} style={styles.saveButton}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      )}

      {/* Review Defaults Tab */}
      {activeTab === 'reviews' && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Client Review Default Settings</h3>
          <p style={styles.description}>
            Default LLM settings for the Client Review system.
            These can be overridden per review type, per template, or per job (admin only).
          </p>

          <div style={styles.formGroup}>
            <label style={styles.label}>Default Provider</label>
            <select
              value={config.reviewDefaultProvider || 'vertex_ai'}
              onChange={(e) => setConfig({ ...config, reviewDefaultProvider: e.target.value })}
              style={styles.select}
            >
              <option value="vertex_ai">Vertex AI</option>
              <option value="gemini">Gemini</option>
              <option value="openai">OpenAI</option>
            </select>
            <p style={styles.hint}>
              The LLM provider used for client review analysis.
            </p>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Default Model</label>
            <select
              value={config.reviewDefaultModel || 'gemini-2.5-flash'}
              onChange={(e) => setConfig({ ...config, reviewDefaultModel: e.target.value })}
              style={styles.select}
            >
              {(config.reviewDefaultProvider === 'openai'
                ? openaiModels
                : config.reviewDefaultProvider === 'gemini'
                  ? geminiModels
                  : vertexModels
              ).map(m => (
                <option key={m.id} value={m.id}>
                  {m.displayName} ({m.contextWindow?.toLocaleString()} tokens)
                </option>
              ))}
            </select>
            <p style={styles.hint}>
              Model used for review analysis. Choose a model with a large context window for comprehensive reviews.
            </p>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>
              Temperature: {config.reviewDefaultTemperature?.toFixed(1) ?? '0.3'}
            </label>
            <input
              type="range"
              min="0"
              max="2"
              step="0.1"
              value={config.reviewDefaultTemperature ?? 0.3}
              onChange={(e) => setConfig({
                ...config,
                reviewDefaultTemperature: parseFloat(e.target.value)
              })}
              style={styles.slider}
            />
            <p style={styles.hint}>
              Lower values (0.1-0.3) produce more consistent, structured output. Higher values allow more creative analysis.
            </p>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Max Output Tokens</label>
            <input
              type="number"
              value={config.reviewDefaultMaxTokens || 8192}
              onChange={(e) => setConfig({
                ...config,
                reviewDefaultMaxTokens: parseInt(e.target.value) || 8192
              })}
              min={100}
              max={32000}
              step={256}
              style={styles.input}
            />
            <p style={styles.hint}>
              Maximum tokens for review analysis output. Higher values allow more detailed reviews (8192 recommended).
            </p>
          </div>

          <div style={{ ...styles.formGroup, borderTop: '1px solid #e0e0e0', paddingTop: '16px', marginTop: '16px' }}>
            <label style={styles.label}>Default Processing Parallelism</label>
            <input
              type="number"
              value={config.clientReviewConfiguration?.processingParallelism || 3}
              onChange={(e) => setConfig({
                ...config,
                clientReviewConfiguration: {
                  ...config.clientReviewConfiguration,
                  processingParallelism: parseInt(e.target.value) || 3
                }
              })}
              min={1}
              max={10}
              style={styles.input}
            />
            <p style={styles.hint}>
              Number of clients processed simultaneously during batch reviews (1-10). Higher values speed up batch reviews but use more resources.
            </p>
          </div>

          <button onClick={handleSave} disabled={saving} style={styles.saveButton}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      )}

      {/* Manage Models Tab */}
      {activeTab === 'models' && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Available Models</h3>
          <p style={styles.description}>
            Manage the list of available models. You can add models, edit their display name and token limits, activate or deactivate them, deprecate or reactivate them, or remove them. Therapist personas choose their model from the active, non-deprecated entries here.
          </p>

          <button
            onClick={() => setShowAddModel(true)}
            style={styles.addButton}
            disabled={saving}
          >
            + Add New Model
          </button>

          {/* Add Model Form */}
          {showAddModel && (
            <div style={styles.modelForm}>
              <h4 style={styles.formTitle}>Add New Model</h4>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Model ID</label>
                  <input
                    type="text"
                    value={newModel.id}
                    onChange={(e) => setNewModel({ ...newModel, id: e.target.value })}
                    placeholder="e.g., gemini-2.5-pro"
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Display Name</label>
                  <input
                    type="text"
                    value={newModel.displayName}
                    onChange={(e) => setNewModel({ ...newModel, displayName: e.target.value })}
                    placeholder="e.g., Gemini 2.5 Pro"
                    style={styles.input}
                  />
                </div>
              </div>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Provider</label>
                  <select
                    value={newModel.provider}
                    onChange={(e) => setNewModel({ ...newModel, provider: e.target.value })}
                    style={styles.select}
                  >
                    <option value="vertex_ai">Vertex AI</option>
                    <option value="gemini">Gemini API</option>
                    <option value="openai">OpenAI</option>
                    <option value="anthropic">Anthropic</option>
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Context Window</label>
                  <input
                    type="number"
                    value={newModel.contextWindow}
                    onChange={(e) => setNewModel({ ...newModel, contextWindow: parseInt(e.target.value) })}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Max Output Tokens</label>
                  <input
                    type="number"
                    value={newModel.maxOutputTokens}
                    onChange={(e) => setNewModel({ ...newModel, maxOutputTokens: parseInt(e.target.value) })}
                    style={styles.input}
                  />
                </div>
              </div>
              <div style={styles.formActions}>
                <button onClick={handleAddModel} disabled={saving} style={styles.saveButton}>
                  {saving ? 'Adding...' : 'Add Model'}
                </button>
                <button
                  onClick={() => setShowAddModel(false)}
                  style={styles.cancelButton}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Models List */}
          <div style={styles.modelsList}>
            {['vertex_ai', 'gemini', 'openai', 'anthropic'].map(provider => {
              const providerModels = (config.availableModels || []).filter(m => m.provider === provider);
              if (providerModels.length === 0) return null;

              return (
                <div key={provider} style={styles.providerSection}>
                  <h4 style={styles.providerTitle}>
                    {provider === 'vertex_ai' ? 'Vertex AI' :
                     provider === 'gemini' ? 'Gemini API' :
                     provider === 'anthropic' ? 'Anthropic' : 'OpenAI'}
                  </h4>
                  {providerModels.map(model => (
                    <div
                      key={`${model.provider}-${model.id}`}
                      style={{
                        ...styles.modelCard,
                        ...(model.isDeprecated ? styles.modelCardDeprecated : {}),
                        ...(!model.isActive ? styles.modelCardInactive : {})
                      }}
                    >
                      <div style={styles.modelInfo}>
                        <span style={styles.modelName}>{model.displayName}</span>
                        <span style={styles.modelId}>{model.id}</span>
                        {model.isDeprecated && (
                          <span style={styles.deprecatedBadge}>Deprecated</span>
                        )}
                        {!model.isActive && !model.isDeprecated && (
                          <span style={styles.inactiveBadge}>Inactive</span>
                        )}
                      </div>
                      {editingModel && editingModel.provider === model.provider && editingModel.id === model.id ? (
                        <div style={styles.modelForm}>
                          <div style={styles.formRow}>
                            <div style={styles.formGroup}>
                              <label style={styles.label}>Display Name</label>
                              <input
                                type="text"
                                value={editingModel.displayName}
                                onChange={(e) => setEditingModel({ ...editingModel, displayName: e.target.value })}
                                style={styles.input}
                              />
                            </div>
                            <div style={styles.formGroup}>
                              <label style={styles.label}>Context Window</label>
                              <input
                                type="number"
                                min="1"
                                value={editingModel.contextWindow}
                                onChange={(e) => setEditingModel({ ...editingModel, contextWindow: e.target.value })}
                                style={styles.input}
                              />
                            </div>
                            <div style={styles.formGroup}>
                              <label style={styles.label}>Max Output Tokens</label>
                              <input
                                type="number"
                                min="1"
                                value={editingModel.maxOutputTokens}
                                onChange={(e) => setEditingModel({ ...editingModel, maxOutputTokens: e.target.value })}
                                style={styles.input}
                              />
                            </div>
                          </div>
                          <div style={styles.hint}>
                            The model ID and provider cannot be changed. To change them, add a new model and remove this one.
                          </div>
                          <div style={styles.formActions}>
                            <button onClick={handleSaveEdit} disabled={saving} style={styles.saveButton}>
                              {saving ? 'Saving...' : 'Save'}
                            </button>
                            <button onClick={() => setEditingModel(null)} disabled={saving} style={styles.cancelButton}>
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div style={styles.modelMeta}>
                            <span>Context: {model.contextWindow?.toLocaleString() || 'N/A'}</span>
                            <span>Max Output: {model.maxOutputTokens?.toLocaleString() || 'N/A'}</span>
                          </div>
                          <div style={styles.modelActions}>
                            <button
                              onClick={() => startEditModel(model)}
                              style={styles.actionButton}
                              disabled={saving}
                            >
                              Edit
                            </button>
                            {model.isDeprecated ? (
                              <button
                                onClick={() => handleReactivateModel(model.provider, model.id)}
                                style={styles.actionButton}
                                disabled={saving}
                              >
                                Reactivate
                              </button>
                            ) : (
                              <button
                                onClick={() => handleUpdateModel(model.provider, model.id, { isActive: !model.isActive })}
                                style={styles.actionButton}
                                disabled={saving}
                              >
                                {model.isActive ? 'Deactivate' : 'Activate'}
                              </button>
                            )}
                            {!model.isDeprecated && (
                              <button
                                onClick={() => handleDeprecateModel(model.provider, model.id)}
                                style={styles.deprecateButton}
                                disabled={saving}
                              >
                                Deprecate
                              </button>
                            )}
                            <button
                              onClick={() => handleRemoveModel(model.provider, model.id)}
                              style={styles.removeButton}
                              disabled={saving}
                            >
                              Remove
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '20px',
    maxWidth: '900px',
    margin: '0 auto',
    fontFamily: 'system-ui, -apple-system, sans-serif'
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    marginBottom: '8px',
    color: '#1a1a2e'
  },
  subtitle: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '24px'
  },
  error: {
    padding: '12px 16px',
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    borderRadius: '6px',
    marginBottom: '16px'
  },
  success: {
    padding: '12px 16px',
    backgroundColor: '#dcfce7',
    color: '#16a34a',
    borderRadius: '6px',
    marginBottom: '16px'
  },
  tabs: {
    display: 'flex',
    gap: '8px',
    marginBottom: '24px',
    borderBottom: '1px solid #e5e7eb',
    paddingBottom: '8px'
  },
  tab: {
    padding: '8px 16px',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    color: '#6b7280',
    borderRadius: '6px 6px 0 0'
  },
  tabActive: {
    color: '#4f46e5',
    fontWeight: '600',
    backgroundColor: '#eef2ff'
  },
  section: {
    backgroundColor: '#fff',
    padding: '24px',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '8px',
    color: '#1a1a2e'
  },
  description: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '24px'
  },
  formGroup: {
    marginBottom: '20px'
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px'
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    marginBottom: '6px',
    color: '#374151'
  },
  toggleLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer'
  },
  select: {
    width: '100%',
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '14px',
    backgroundColor: '#fff'
  },
  input: {
    width: '100%',
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '14px',
    boxSizing: 'border-box'
  },
  slider: {
    width: '100%',
    marginTop: '8px'
  },
  checkbox: {
    width: '18px',
    height: '18px'
  },
  hint: {
    fontSize: '12px',
    color: '#9ca3af',
    marginTop: '4px'
  },
  saveButton: {
    padding: '10px 20px',
    backgroundColor: '#4f46e5',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500'
  },
  addButton: {
    padding: '8px 16px',
    backgroundColor: '#10b981',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    marginBottom: '16px'
  },
  cancelButton: {
    padding: '10px 20px',
    backgroundColor: '#f3f4f6',
    color: '#374151',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  modelForm: {
    backgroundColor: '#f9fafb',
    padding: '16px',
    borderRadius: '8px',
    marginBottom: '24px'
  },
  formTitle: {
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '16px'
  },
  formActions: {
    display: 'flex',
    gap: '12px',
    marginTop: '16px'
  },
  modelsList: {
    marginTop: '16px'
  },
  providerSection: {
    marginBottom: '24px'
  },
  providerTitle: {
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '12px',
    color: '#374151'
  },
  modelCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    backgroundColor: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    marginBottom: '8px'
  },
  modelCardDeprecated: {
    backgroundColor: '#fef3c7',
    borderColor: '#fcd34d'
  },
  modelCardInactive: {
    backgroundColor: '#f3f4f6',
    opacity: 0.7
  },
  modelInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  modelName: {
    fontWeight: '500',
    color: '#1a1a2e'
  },
  modelId: {
    fontSize: '12px',
    color: '#9ca3af',
    fontFamily: 'monospace'
  },
  deprecatedBadge: {
    padding: '2px 8px',
    backgroundColor: '#fef3c7',
    color: '#92400e',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '500'
  },
  inactiveBadge: {
    padding: '2px 8px',
    backgroundColor: '#f3f4f6',
    color: '#6b7280',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '500'
  },
  modelMeta: {
    display: 'flex',
    gap: '16px',
    fontSize: '12px',
    color: '#6b7280'
  },
  modelActions: {
    display: 'flex',
    gap: '8px'
  },
  actionButton: {
    padding: '4px 12px',
    backgroundColor: '#fff',
    color: '#4f46e5',
    border: '1px solid #4f46e5',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px'
  },
  deprecateButton: {
    padding: '4px 12px',
    backgroundColor: '#fff',
    color: '#d97706',
    border: '1px solid #d97706',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px'
  },
  removeButton: {
    padding: '4px 12px',
    backgroundColor: '#fff',
    color: '#dc2626',
    border: '1px solid #dc2626',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px'
  }
};

export default LLMConfigurationManager;
